import { formatShortDuration } from "../../Logic/emsDates";
import { BEHIND_RUN, SLIPPING_RUN, SNAPSHOT_INTERVAL_SECONDS } from "../backlogTrend";
import { TenantCollectionHealth, TenantMailProcessingRow } from "../CoreAPI";
import {
    overviewLastReadAt,
    rowFailed,
    rowGivenUp,
    rowInFlight,
    StalledQueue,
    stalledQueues,
    sumMeasured,
} from "../derive";
import { TenantStanding, tenantStanding } from "../tenantHealth";
import { TrendSeries } from "../trendSeries";
import Measured from "./Measured";
import Sparkline from "./Sparkline";

interface iTenantTableProps {
    /** Server-ordered worst-first. Rendered in the order given. */
    rows: TenantMailProcessingRow[];
    /**
     * tenantKey to backlog rising-run length.
     *
     * Presence in this map is not itself a verdict: a run counts as "behind"
     * only when paired with `collectionHealth === "Healthy"` and
     * `run >= SLIPPING_RUN`. `attentionLines` applies that identical guard to
     * the same map, so the two views agree about which tenants are flagged —
     * neither trusts mere presence in the map on its own. A stale, failing, or
     * disabled tenant may still have an entry here — the map alone is not
     * sufficient to mark a row behind.
     */
    behindRuns: Map<string, number>;
    /** tenantKey to 24-hour series. A key is absent until its history arrives. */
    trends: Map<string, TrendSeries>;
    /** Tenants whose history could not be read, as opposed to not yet arrived. */
    trendFailures: Set<string>;
    /**
     * Open a tenant's detail view. Receives the tenant's key and its display
     * name — the name so the detail header can render before its own request
     * resolves.
     *
     * REQUIRED, not optional. An optional callback would let a caller render
     * a table whose tenant names look clickable and do nothing, which is a
     * failure nobody notices in review.
     */
    onInspect: (tenantKey: string, name: string) => void;
}

/**
 * The dot's colour, per standing.
 *
 * The same five classes MailboxRow's STANDING_DOT uses, and deliberately so:
 * a dot beside a tenant and a dot beside a mailbox now mean the same thing —
 * "is something wrong here" — rather than the tenant one quietly meaning "did
 * the collector manage to read this".
 *
 * `unknownHealth` gets the bare `mp-dot`: no colour is the honest answer when
 * the server reports a state this client has never heard of. Guessing green
 * there would be the "nothing is known means nothing is wrong" defect.
 */
const TENANT_DOT: Record<TenantStanding, string> = {
    collectionFailing: "mp-dot mp-dot-fail",
    mailboxFault: "mp-dot mp-dot-fail",
    collectionStale: "mp-dot mp-dot-stale",
    stalled: "mp-dot mp-dot-stale",
    neverCollected: "mp-dot mp-dot-never",
    unknownHealth: "mp-dot",
    waiting: "mp-dot mp-dot-ok",
    healthy: "mp-dot mp-dot-ok",
    disabled: "mp-dot mp-dot-off",
};

/**
 * What the dot says on hover and announces to a screen reader.
 *
 * NOT the bare `collectionHealth` enum any more. It used to be, and "Healthy"
 * was then announced for a tenant with three unhealthy mailboxes and a stalled
 * queue — true of the collector, false of the tenant, and the dot is read as a
 * verdict on the tenant. Every label below names its own subject.
 */
const TENANT_LABEL: Record<TenantStanding, string> = {
    collectionFailing: "Collection failing",
    collectionStale: "Collection stale",
    neverCollected: "Never collected",
    unknownHealth: "Health unknown",
    mailboxFault: "Mailboxes unhealthy",
    stalled: "Stalled",
    waiting: "Waiting",
    healthy: "OK",
    disabled: "Off",
};

/** A short label for the states where the row's own figures cannot be trusted. */
const HEALTH_NOTE: Partial<Record<TenantCollectionHealth, string>> = {
    Stale: "figures are old — collector suspect",
    NeverCollected: "never collected",
    Disabled: "collection disabled",
};

function rowClassName(row: TenantMailProcessingRow, behind: boolean): string {
    if (row.collectionHealth === "Failing") {
        return "mp-row-fail";
    }
    if (behind) {
        return "mp-row-behind";
    }
    if (row.collectionHealth === "Stale") {
        return "mp-row-stale";
    }
    if (row.collectionHealth === "Disabled" || row.collectionHealth === "NeverCollected") {
        return "mp-row-off";
    }
    return "";
}

/**
 * Formats the landing view's "last read" stamp.
 *
 * Returns undefined rather than a placeholder string when the tenant has
 * never been read, so the caller can route that case through `Measured` and
 * keep the same absent-value affordance (dotted underline, title) that every
 * other unmeasured cell in this table gets.
 *
 * Deliberately local to this component and named for the landing meaning. Do
 * NOT reuse it for a history row's timestamp: the same `lastAttemptAt` field
 * means "that snapshot's measurement instant" there, and a shared formatter
 * would label a per-tick fact as a cross-row one. See spec §3.1.
 */
function formatLastRead(row: TenantMailProcessingRow): string | undefined {
    const read = overviewLastReadAt(row);
    if (!read) {
        return undefined;
    }
    return read.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The oldest age across whichever queues the server flagged as stalled.
 *
 * Only stalled queues get an age, and only when the server sent a parseable
 * one — `oldestAge` is optional, so a stalled queue can legitimately have no
 * usable age at all. That case is handled by the caller, not folded into
 * "nothing stalled" here: a stall the table cannot age is still a stall.
 */
function oldestStalledAge(stalled: StalledQueue[]): number | undefined {
    const ages = stalled.map(queue => queue.oldestAgeSeconds).filter((age): age is number => age !== undefined);
    return ages.length === 0 ? undefined : Math.max(...ages);
}

function TenantTable({ rows, behindRuns, trends, trendFailures, onInspect }: iTenantTableProps) {
    if (rows.length === 0) {
        return (
            <div className="mp-empty">
                No tenants were returned. The roster is server configuration, so this is unexpected — check the
                collector configuration rather than this page.
            </div>
        );
    }

    const backlogTotal = sumMeasured(rows.map(row => row.mailboxes?.backlog));
    const inFlightTotal = sumMeasured(rows.map(rowInFlight));
    const failedTotal = sumMeasured(rows.map(rowFailed));
    const givenUpTotal = sumMeasured(rows.map(rowGivenUp));
    const mailboxTotal = sumMeasured(rows.map(row => row.mailboxes?.count));
    const unhealthyTotal = sumMeasured(rows.map(row => row.mailboxes?.unhealthyCount));

    // Throughput sums across tenants because tenants are disjoint populations
    // measured over the same interval. Contract §2.6 forbids summing a gauge
    // across TIME, which this table never does — every column here is one
    // instant.
    const receivedTotal = sumMeasured(rows.map(row => row.throughput?.received));
    const sentTotal = sumMeasured(rows.map(row => row.throughput?.sent));

    return (
        <div className="mp-table-scroll">
            <table className="mp-table">
                <thead>
                    <tr>
                        <th rowSpan={2}>Tenant</th>
                        <th
                            colSpan={3}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Waiting
                        </th>
                        <th
                            colSpan={2}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Stopped
                        </th>
                        <th
                            colSpan={2}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Mailboxes
                        </th>
                        <th
                            colSpan={2}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Flow 5m
                        </th>
                        {/* "Last read", not "Collected": on the landing view this is when the
                            tenant was last successfully measured, not a per-tick collection stamp. */}
                        <th
                            rowSpan={2}
                            className="mp-boundary mp-last-read"
                        >
                            Last read
                        </th>
                        <th
                            colSpan={1}
                            scope="colgroup"
                            className="mp-boundary mp-flow24"
                        >
                            Flow 24h
                        </th>
                    </tr>
                    <tr>
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            Backlog
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            In-flight
                        </th>
                        <th
                            scope="col"
                            className="mp-oldest"
                        >
                            Oldest
                        </th>
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            Failed
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Given up
                        </th>
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            Total
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Bad
                        </th>
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            In
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Out
                        </th>
                        {/*
                            The column's colour key. Two 1.1px lines in a 20px cell cannot carry a
                            legend, and the chart's legend sits above a table that scrolls
                            horizontally — so by the time an operator is scanning this column the
                            page-level key may be off-screen. Four words in the header they are
                            already reading is the cheapest place to say it.
                        */}
                        <th
                            scope="col"
                            className="mp-flow24"
                        >
                            <span className="mp-spark-key-in">in</span> / <span className="mp-spark-key-out">out</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        // React key / display fallback: any distinguishing string will do.
                        const rowKey = row.tenantKey ?? row.name ?? "";
                        // behindRuns is keyed strictly on tenantKey (see attentionLines,
                        // which looks it up the same way) — a row with no tenantKey has
                        // no entry, full stop. Falling back to `name` here would let this
                        // lookup silently disagree with attentionLines' for the same row.
                        const run = behindRuns.get(row.tenantKey ?? "");
                        // A run only counts as "behind" for a Healthy tenant that has
                        // been rising for at least SLIPPING_RUN steps — see the
                        // behindRuns doc comment above for why mere presence in the
                        // map is not enough.
                        const behind = run !== undefined && run >= SLIPPING_RUN && row.collectionHealth === "Healthy";
                        const note = row.collectionHealth ? HEALTH_NOTE[row.collectionHealth] : undefined;
                        const stalled = stalledQueues(row);
                        const stalledAge = oldestStalledAge(stalled);
                        // Narrowed once here rather than at each use: TS narrowing does not
                        // survive into the onClick closure, so the alternative is two non-null
                        // assertions and a reader deriving that the label and the callback agree.
                        const tenantKey = row.tenantKey;
                        // Named once: the dot shows this on hover and announces
                        // it to a screen reader, and the two must not drift.
                        const standing = tenantStanding(row);
                        const healthLabel = TENANT_LABEL[standing];

                        return (
                            <tr
                                key={rowKey}
                                className={rowClassName(row, behind)}
                            >
                                <td>
                                    {/*
                                        Collection health has no column — every
                                        other cell is a figure — so the dot is
                                        the only thing that states it, and
                                        `role="img"` plus `aria-label` is what
                                        gets it to a screen reader. `title` on an
                                        empty span does not: no role, no text,
                                        nothing for an accessible name to attach
                                        to. Same treatment as MailboxRow's dot.
                                    */}
                                    <span
                                        role="img"
                                        aria-label={healthLabel}
                                        className={TENANT_DOT[standing]}
                                        title={healthLabel}
                                    />
                                    {/*
                                        Only a button when there is a tenantKey to inspect with —
                                        the key is what the STS token call takes, so a row without
                                        one cannot be opened. Plain text rather than a control that
                                        does nothing.

                                        Every keyed row is clickable regardless of health: spec
                                        §2.3 discovers entitlement on click rather than
                                        pre-filtering the roster.
                                    */}
                                    {tenantKey ? (
                                        <button
                                            type="button"
                                            className="mp-tenant-btn"
                                            onClick={() => onInspect(tenantKey, row.name ?? tenantKey)}
                                        >
                                            <span className="mp-tenant-name">{row.name ?? tenantKey}</span>
                                        </button>
                                    ) : (
                                        <span className="mp-tenant-name">{row.name ?? rowKey}</span>
                                    )}
                                    {/* Only render the key separately when it says something the
                                        name didn't already: with no tenantKey, rowKey falls back to
                                        the same name just rendered above, and repeating it verbatim
                                        would be a plain duplicate, not a second piece of information. */}
                                    {row.tenantKey && (
                                        <>
                                            {" "}
                                            <span className="mp-tenant-key">{row.tenantKey}</span>
                                        </>
                                    )}
                                    {row.error && <div className="mp-row-error">{row.error}</div>}
                                    {!row.error && note && <div className="mp-row-note">{note}</div>}
                                </td>
                                <td className="mp-r mp-boundary">
                                    <Measured value={row.mailboxes?.backlog} />
                                    {behind && (
                                        <span className={run! >= BEHIND_RUN ? "mp-rise mp-rise-high" : "mp-rise"}>
                                            {`▲${formatShortDuration(run! * SNAPSHOT_INTERVAL_SECONDS)}`}
                                        </span>
                                    )}
                                </td>
                                <td className="mp-r">
                                    <Measured value={rowInFlight(row)} />
                                </td>
                                {/*
                                    Three distinct states, and only one of them is an em dash.

                                    Reserving `—` for "not measured" across the entire table is
                                    deliberate. An earlier draft rendered `—` here for "no queue is
                                    stalled" too, which gave one glyph two meanings in one table,
                                    separated only by a tooltip nobody hovers. Contract §2.3 is
                                    explicit that staleness is a positive finding only, so its
                                    absence is not a measurement gap and must not look like one.

                                    A stalled queue whose age could not be parsed is still a
                                    stall — it gets the pill without a duration rather than
                                    silently falling into "nothing stalled".
                                */}
                                <td className="mp-oldest">
                                    {stalled.length > 0 ? (
                                        <span className="mp-stall-pill">
                                            {stalledAge !== undefined ? formatShortDuration(stalledAge) : "stalled"}
                                        </span>
                                    ) : row.inFlight === undefined ? (
                                        <Measured value={undefined} />
                                    ) : null}
                                </td>
                                <td className="mp-r mp-boundary">
                                    <Measured value={rowFailed(row)} />
                                </td>
                                <td className="mp-r">
                                    <Measured value={rowGivenUp(row)} />
                                </td>
                                <td className="mp-r mp-boundary">
                                    <Measured value={row.mailboxes?.count} />
                                </td>
                                <td className="mp-r">
                                    <Measured value={row.mailboxes?.unhealthyCount} />
                                </td>
                                <td className="mp-r mp-boundary">
                                    <Measured value={row.throughput?.received} />
                                </td>
                                <td className="mp-r">
                                    <Measured value={row.throughput?.sent} />
                                </td>
                                <td className="mp-last-read">
                                    {formatLastRead(row) ?? <Measured value={undefined} />}
                                    {row.lastOutcome && row.lastOutcome !== "Success" && (
                                        <span className="mp-outcome"> {row.lastOutcome}</span>
                                    )}
                                </td>
                                <td className="mp-flow24">
                                    {trendFailures.has(rowKey) ? (
                                        <span
                                            className="mp-absent"
                                            title="This tenant's history could not be read"
                                        >
                                            no data
                                        </span>
                                    ) : (
                                        <Sparkline series={trends.get(rowKey)} />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        {/*
                            Deliberately generic. Each total below covers its own count of
                            tenants — not necessarily all of `rows.length`, and not
                            necessarily the same count as its neighbour — so a single
                            row-wide sentence here would state one column's coverage while
                            visually describing all nine. Each cell carries its own coverage
                            as a title instead (see the `Measured` calls below).
                        */}
                        <td className="mp-total-label">TOTAL</td>
                        <td className="mp-r mp-boundary">
                            <Measured
                                value={backlogTotal.total}
                                title={`Backlog measured for ${backlogTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r">
                            <Measured
                                value={inFlightTotal.total}
                                title={`In-flight measured for ${inFlightTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        {/*
                            Empty, not a dash. "Oldest age" has no meaningful total —
                            the oldest of a set of oldest-ages is not a number anyone
                            asked for. A dash here would be a THIRD meaning for the
                            same glyph, after "not measured" in the body cells and
                            "nothing stalled" (which is now also empty).
                        */}
                        <td className="mp-oldest" />
                        <td className="mp-r mp-boundary">
                            <Measured
                                value={failedTotal.total}
                                title={`Failed measured for ${failedTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r">
                            <Measured
                                value={givenUpTotal.total}
                                title={`Given up measured for ${givenUpTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r mp-boundary">
                            <Measured
                                value={mailboxTotal.total}
                                title={`Mailbox total measured for ${mailboxTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r">
                            <Measured
                                value={unhealthyTotal.total}
                                title={`Unhealthy mailboxes measured for ${unhealthyTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r mp-boundary">
                            <Measured
                                value={receivedTotal.total}
                                title={`Received measured for ${receivedTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td className="mp-r">
                            <Measured
                                value={sentTotal.total}
                                title={`Sent measured for ${sentTotal.measured} of ${rows.length} tenants`}
                            />
                        </td>
                        <td />
                        {/*
                            Empty, not a dash. There is no total of forty sparklines, and the em dash
                            means "not measured" and nothing else in this table — the same reasoning
                            as the Oldest footer cell above.
                        */}
                        <td className="mp-flow24" />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

export default TenantTable;
